import { IconFileTypePdf, IconX } from '@tabler/icons-react';

function PdfFileBadge({ className = '' }) {
    return (
        <div
            className={`flex h-[42px] w-[34px] flex-none items-center justify-center rounded-md bg-pdf-cat-tint text-[9px] font-bold text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400 ${className}`}
        >
            PDF
        </div>
    );
}

function rowShellClass(selected, clickable) {
    const base = 'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 transition-colors dark:bg-zinc-900';
    if (selected) {
        return `${base} border-pdf-cat-solid bg-pdf-cat-tint/40 dark:border-orange-500 dark:bg-orange-950/30`;
    }
    if (clickable) {
        return `${base} cursor-pointer border-[#ECE8E4] bg-white hover:border-pdf-cat-solid/40 hover:bg-[#F7F5F3] dark:border-zinc-700 dark:hover:bg-zinc-800`;
    }
    return `${base} border-[#ECE8E4] bg-white dark:border-zinc-700`;
}

export function PdfUploadedFile({
    name,
    meta,
    onRemove,
    removeLabel,
    selected = false,
    onSelect,
}) {
    const clickable = typeof onSelect === 'function';

    return (
        <div
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onSelect : undefined}
            onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            } : undefined}
            className={rowShellClass(selected, clickable)}
        >
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-pdf-cat-tint text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400">
                <IconFileTypePdf size={20} stroke={1.7} />
            </div>
            <div className="min-w-0 flex-1 leading-snug">
                <div className="truncate text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">{name}</div>
                {meta ? (
                    <div className="text-[11px] text-[#A8A099] dark:text-zinc-500">{meta}</div>
                ) : null}
            </div>
            {onRemove ? (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    aria-label={removeLabel}
                    className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-[#F7F5F3] text-[#6B635C] transition-colors hover:bg-pdf-cat-tint hover:text-[#D33A2C] dark:bg-zinc-800 dark:text-zinc-400"
                >
                    <IconX size={15} stroke={2} />
                </button>
            ) : null}
        </div>
    );
}

export function PdfFileRow({
    name,
    meta,
    onRemove,
    removeLabel,
    dragHandle,
    reorderUp,
    reorderDown,
    upDisabled,
    downDisabled,
    upLabel,
    downLabel,
    selected = false,
    onSelect,
}) {
    const clickable = typeof onSelect === 'function';
    const moveBtnClass = (disabled) =>
        `flex h-5 w-[22px] items-center justify-center rounded-md border-none p-0 ${
            disabled
                ? 'cursor-default bg-[#F7F5F3] text-[#A8A099] opacity-50 dark:bg-zinc-800'
                : 'cursor-pointer bg-[#F7F5F3] text-[#3D3833] hover:bg-[#ECE8E4] dark:bg-zinc-800 dark:text-zinc-300'
        }`;

    return (
        <div
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onSelect : undefined}
            onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            } : undefined}
            className={`animate-[toolSlide_0.4s_cubic-bezier(0.2,0.8,0.25,1)_both] ${rowShellClass(selected, clickable).replace('px-3 py-2.5', 'px-2.5 py-2')}`}
        >
            {dragHandle ? (
                <span
                    className="flex-none cursor-grab text-[#A8A099] active:cursor-grabbing"
                    aria-hidden="true"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    {dragHandle}
                </span>
            ) : null}
            <PdfFileBadge />
            <div className="min-w-0 flex-1 leading-snug">
                <div className="truncate text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">{name}</div>
                {meta ? (
                    <div className="text-[11px] text-[#A8A099] dark:text-zinc-500">{meta}</div>
                ) : null}
            </div>
            {reorderUp || reorderDown ? (
                <div className="flex flex-none flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={reorderUp}
                        disabled={upDisabled}
                        aria-label={upLabel}
                        className={moveBtnClass(upDisabled)}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 15l6-6 6 6" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={reorderDown}
                        disabled={downDisabled}
                        aria-label={downLabel}
                        className={moveBtnClass(downDisabled)}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </button>
                </div>
            ) : null}
            {onRemove ? (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    aria-label={removeLabel}
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-[#F7F5F3] text-[#6B635C] transition-colors hover:bg-pdf-cat-tint hover:text-[#D33A2C] dark:bg-zinc-800 dark:text-zinc-400"
                >
                    <IconX size={14} stroke={2} />
                </button>
            ) : null}
        </div>
    );
}
